import { useRef, useState, useEffect } from 'react';

import { cn } from '@app/libs/utils';

interface DropPanelProps {
  children: React.ReactNode;
  className?: string;
  triggerClassName?: string;
  dropdownClassName?: string;
  trigger: React.ReactNode;
  align?: 'left' | 'right';
}

export const DropPanel: React.FC<DropPanelProps> = ({
  children,
  className,
  triggerClassName,
  dropdownClassName,
  trigger,
  align = 'right',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ left?: number; right?: number }>({});
  const dropdownRef = useRef<HTMLDivElement>(null);

  const calculateDropdownPosition = () => {
    if (!dropdownRef.current) return;

    const rect = dropdownRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const dropdownWidth = 256; // w-64 = 256px

    if (align === 'left') {
      setDropdownPosition({ left: 0 });
    } else {
      // Right align - check if dropdown fits on the right
      const spaceOnRight = viewportWidth - rect.right;
      if (spaceOnRight >= dropdownWidth) {
        setDropdownPosition({ left: 0 });
      } else {
        // Align to right edge of trigger
        setDropdownPosition({ right: 0 });
      }
    }
  };

  const handleToggle = () => {
    if (!isOpen) {
      calculateDropdownPosition();
    }
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as any;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    const handleResize = () => {
      calculateDropdownPosition();
    };

    // Use capture phase to ensure we catch the event before other handlers
    document.addEventListener('mousedown', handleClickOutside, true);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', handleResize);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen, align]);

  return (
    <div ref={dropdownRef} className={cn('relative', className)}>
      <div
        className={cn(
          'cursor-pointer group flex justify-between items-center hover:shadow-md rounded-lg',
          triggerClassName,
          isOpen && 'open'
        )}
        onClick={handleToggle}
      >
        {trigger}
      </div>
      {isOpen && (
        <div
          className={cn(
            'absolute top-full w-full bg-white border border-gray-200 rounded-xl shadow-lg z-1000 p-4 mt-1 gap-4 flex flex-col',
            align === 'left' ? 'left-0' : 'right-0',
            dropdownClassName,
            isOpen && 'open'
          )}
          style={dropdownPosition}
          data-dropdown
        >
          {children}
        </div>
      )}
    </div>
  );
};
